import { z } from 'zod'

/**
 * Client-side validation.
 *
 * Mirrors the server rules for fast feedback, but is NEVER the enforcement
 * point — every one of these is re-validated by a FormRequest.
 */

export const loginSchema = z.object({
  email: z.string().min(1, 'Enter your email').email('That does not look like an email'),
  password: z.string().min(1, 'Enter your password'),
})

export const registerSchema = z
  .object({
    business_name: z.string().min(2, 'Enter your business name').max(120),
    name: z.string().min(2, 'Enter your name').max(120),
    email: z.string().min(1, 'Enter your email').email('That does not look like an email'),
    phone: z.string().max(32).optional().or(z.literal('')),
    password: z.string().min(8, 'At least 8 characters'),
    password_confirmation: z.string().min(1, 'Confirm your password'),
    // No .default() here: it makes the zod INPUT type optional while the
    // output stays required, and react-hook-form types the form against the
    // input. The default lives in defaultValues instead.
    country_code: z.string().length(2),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  })

export const acceptInvitationSchema = z
  .object({
    name: z.string().max(120).optional().or(z.literal('')),
    password: z.string().min(8, 'At least 8 characters'),
    password_confirmation: z.string().min(1, 'Confirm your password'),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  })

export const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'Enter your email').email('That does not look like an email'),
})

/**
 * The token and email come from the link, not from the person, so they are not
 * fields here — the form only collects the new password. They are validated by
 * the server, which is the only place that can actually check them.
 */
export const resetPasswordSchema = z
  .object({
    password: z.string().min(8, 'At least 8 characters'),
    password_confirmation: z.string().min(1, 'Confirm your password'),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  })

export const profileSchema = z.object({
  name: z.string().min(2, 'Enter your name').max(120),
  phone: z.string().max(32).optional().or(z.literal('')),
})

/**
 * The current password is required and NOT length-checked.
 *
 * It is verified server-side by the `current_password` rule; adding a minimum
 * here would reject a legitimate old password set before the policy changed,
 * and tell somebody their real password is invalid.
 */
export const changePasswordSchema = z
  .object({
    current_password: z.string().min(1, 'Enter your current password'),
    password: z.string().min(8, 'At least 8 characters'),
    password_confirmation: z.string().min(1, 'Confirm your new password'),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  })

export type LoginValues = z.infer<typeof loginSchema>
export type RegisterValues = z.infer<typeof registerSchema>
export type AcceptInvitationValues = z.infer<typeof acceptInvitationSchema>
export type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>
export type ResetPasswordValues = z.infer<typeof resetPasswordSchema>
export type ProfileValues = z.infer<typeof profileSchema>
export type ChangePasswordValues = z.infer<typeof changePasswordSchema>
